import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {MatSelectChange} from '@angular/material/select';
import {ReplaySubject} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil
} from 'rxjs/operators';

import {
  DobryAgentBlogCategory
} from '../../interface/dobry-agent-blog-article';

import {
  DobryAgentBlogCategoryService
} from '../../services/dobry-agent-blog-category.service';

import {
  ListResponse
} from '../../interface/response/list-response';

@Component({
  selector: 'app-dict-dobry-agent-blog-categories',
  templateUrl: './dict-dobry-agent-blog-categories.component.html',
  styleUrls: ['./dict-dobry-agent-blog-categories.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DictDobryAgentBlogCategoriesComponent)
    }
  ]
})
export class DictDobryAgentBlogCategoriesComponent
  implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label = 'Kategoria';
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() onlyActive = true;

  @Output()
  selectedCategory: EventEmitter<DobryAgentBlogCategory>
    = new EventEmitter<DobryAgentBlogCategory>();

  @Output()
  selectedCategories: EventEmitter<DobryAgentBlogCategory[]>
    = new EventEmitter<DobryAgentBlogCategory[]>();

  externalControl = true;
  disabled = false;

  allItems: DobryAgentBlogCategory[] = [];
  items: DobryAgentBlogCategory[] = [];

  value?: string | string[] = null;

  categoryFilterCtrl: FormControl;

  private destroyed$: ReplaySubject<boolean>
    = new ReplaySubject<boolean>(1);

  constructor(
    private blogCategoryService: DobryAgentBlogCategoryService,
    private fb: FormBuilder,
  ) {
    this.categoryFilterCtrl = this.fb.control('');
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl(this.multiple ? [] : '');
      this.externalControl = false;
    }

    this.blogCategoryService
      .getBlogCategoryNotCountedListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: ListResponse<DobryAgentBlogCategory>) => {
        this.allItems = response.items ?? [];
        this.items = [...this.allItems];

        this.emitSelectedItems();
      });

    this.categoryFilterCtrl.valueChanges
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value: string | null) => {
        this.filterItems(value);
      });

    this.loadDefaultList();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: string | string[] | null): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(event: MatSelectChange): void {
    this.value = event.value;
    this.onChange(this.value);

    this.emitSelectedItems();
  }

  clearValue(): void {
    const value = this.multiple ? [] : '';

    this.control.setValue(value);
    this.writeValue(value);
    this.onChange(value);

    if (this.multiple) {
      this.selectedCategories.emit([]);
    }
  }

  private loadDefaultList(): void {
    this.blogCategoryService.loadNotCountedList(
      this.onlyActive
        ? {active: true}
        : {}
    );
  }

  private filterItems(value: string | null): void {
    const search = (value ?? '')
      .trim()
      .toLocaleLowerCase('pl-PL');

    if (!search) {
      this.items = [...this.allItems];
      return;
    }

    this.items = this.allItems.filter(
      (category: DobryAgentBlogCategory) => {
        const name = (category.name ?? '')
          .trim()
          .toLocaleLowerCase('pl-PL');

        return name.includes(search);
      }
    );
  }

  private emitSelectedItems(): void {
    if (this.multiple) {
      const selected = this.allItems.filter(
        (category: DobryAgentBlogCategory) =>
          this.control.value?.includes(category.uuid)
      );

      this.selectedCategories.emit(selected);
      return;
    }

    const selected = this.allItems.find(
      (category: DobryAgentBlogCategory) =>
        category.uuid === this.control.value
    );

    if (selected) {
      this.selectedCategory.emit(selected);
    }
  }
}
